import { useCallback, useEffect, useState, type DependencyList } from 'react'

export interface ResourceState<T> {
  data: T | null
  loading: boolean
  error: string | null
  /** Re-run the fetcher (e.g. after a mutation). */
  reload: () => void
}

/**
 * Skeleton/spinner is suppressed for fetches that resolve faster than this —
 * cached or near-instant responses shouldn't flash a loading state
 * (see the loading-states skill).
 */
const SKELETON_DELAY_MS = 200

/**
 * Generic loading/error/data fetch. The single place pages handle the three
 * async states, so components don't re-roll `useState` triads.
 *
 * `fetcher` is intentionally NOT a dependency — pass the values it closes over
 * in `deps` (the same contract as `useEffect`), so an inline fetcher doesn't
 * re-run every render. The in-flight result of a superseded fetch is ignored.
 */
export function useResource<T>(
  fetcher: () => Promise<T>,
  deps: DependencyList = [],
  errorMessage = 'Something went wrong.',
): ResourceState<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    let active = true
    setError(null)
    const timer = setTimeout(() => {
      if (active) setLoading(true)
    }, SKELETON_DELAY_MS)

    fetcher()
      .then((result) => {
        if (active) setData(result)
      })
      .catch(() => {
        if (active) setError(errorMessage)
      })
      .finally(() => {
        if (!active) return
        clearTimeout(timer)
        setLoading(false)
      })

    return () => {
      active = false
      clearTimeout(timer)
    }
    // fetcher is excluded by design; callers declare its inputs via `deps`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  return { data, loading, error, reload }
}
